import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  Container,
  Paper,
  IconButton,
  Chip,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import default_theme from "assets/themes/themes";
import { fetchLAPDetailRequest } from "../../../redux/lap/lapSlice";

const FIELD_COLOR = default_theme.palette.other.chatGreen;

const LAPDetailPage = () => {
  const { lapId, dynamoCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const lapState = useSelector((state) => state.lap);
  const authState = useSelector((state) => state.auth);
  const lap = lapId ? lapState.lapDetails[lapId] : null;
  const loading = lapState.loading;
  const isAuthenticated = authState?.isAuthenticated ?? false;
  
  const [favorites, setFavorites] = useState(new Set());

  // Load favorites from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("lapFavorites");
    if (saved) setFavorites(new Set(JSON.parse(saved)));
  }, []);

  // Fetch the LAP by ID
  useEffect(() => {
    if (lapId) {
      dispatch(fetchLAPDetailRequest({ lapId }));
    }
  }, [lapId, dispatch]);

  const toggleFavorite = (lapId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    const next = new Set(favorites);
    next.has(lapId) ? next.delete(lapId) : next.add(lapId);
    setFavorites(next);
    localStorage.setItem("lapFavorites", JSON.stringify([...next]));
  };

  const handleBackClick = () => {
    // If this LAP was opened from a specific Dynamo, go back to that Dynamo page.
    if (dynamoCode) {
      navigate(`/solutions/rural-development/lap-dynamo/${dynamoCode}`);
      return;
    }

    // Fallback: original behaviour (back to LAP catalog list)
    if (location.state?.fromCatalogList) {
      navigate("/solutions/rural-development/lap-catalog", {
        state: {
          searchQuery: location.state.searchQuery,
          selectedFilters: location.state.selectedFilters,
          currentPage: location.state.currentPage,
        },
      });
    } else {
      navigate("/solutions/rural-development/lap-catalog");
    }
  };

  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (typeof value === "boolean") return true;
    return true;
  };

  // Use a safe fallback when LAP data is not yet loaded so hooks can run unconditionally
  const data = lap?.combinedData || {};

  // Fields from the LAP table to expose in the side menu
  const sideMenuFields = [
    { key: "brief_description", label: "Brief Description" },
    { key: "objectives_of_the_solution", label: "Objectives of the Solution" },
    { key: "relevant_rdd_and_rdd_subcategory", label: "Relevant RDD and RDD Subcategory" },
    { key: "relevant_challenges", label: "Relevant Challenges" },
    { key: "specific_activities", label: "Specific Activities" },
    { key: "targets", label: "Targets" },
    { key: "location_of_implementation", label: "Location of Implementation" },
    { key: "geography_and_territorial_context", label: "Geography and Territorial Context" },
    { key: "integration_of_relevant_crosscutting", label: "Integration of Relevant Crosscutting" },
    { key: "forms_of_innovation_considered", label: "Forms of Innovation Considered" },
    { key: "gender_sensitive_planning_aspects", label: "Gender Sensitive Planning Aspects" },
    { key: "resources_capitals_needed", label: "Resources and Capitals Needed" },
    { key: "main_stakeholders_involved_and_their_contribution", label: "Main Stakeholders and Contribution" },
    { key: "main_and_other_beneficiaries", label: "Main and Other Beneficiaries" },
    { key: "target_groups_at_risk_of_exclusion", label: "Target Groups at Risk of Exclusion" },
    { key: "timeframe_m_to_m", label: "Timeframe (M to M)" },
    { key: "indicative_cost", label: "Indicative Cost" },
    { key: "indicative_funding_sources", label: "Indicative Funding Sources" },
    { key: "long_term_impact_assessment", label: "Long Term Impact Assessment" },
    { key: "communication_and_engagement", label: "Communication and Engagement" },
    { key: "sustainability_consideration", label: "Sustainability Consideration" },
    { key: "synergies_with_other_solutions", label: "Synergies with Other Solutions" },
    { key: "synergies_with_local_policies", label: "Synergies with Local Policies" },
    { key: "synergies_with_eu_policies_when_relevant", label: "Synergies with EU Policies (when relevant)" },
  ];

  // Hook must be unconditional; initialise with null and then derive value once data is loaded
  const [selectedFieldKey, setSelectedFieldKey] = useState(null);

  // Show only fields that actually have content
  const availableSideFields = sideMenuFields.filter((field) =>
    hasValue(data[field.key])
  );

  // All content is publicly viewable, regardless of authentication
  const visibleSideFields = availableSideFields;

  // Ensure we always have a selected field once data is available
  useEffect(() => {
    if (!visibleSideFields.length) return;

    // Prefer Brief Description, otherwise first available
    const preferred =
      visibleSideFields.find((f) => f.key === "brief_description") ||
      visibleSideFields[0];

    setSelectedFieldKey((prev) => prev || preferred.key);
  }, [lapId, visibleSideFields.length]);

  const selectedField =
    visibleSideFields.find((f) => f.key === selectedFieldKey) ||
    visibleSideFields[0] ||
    null;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!lap || !lap.combinedData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5">LAP not found</Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ flex: 1, backgroundColor: default_theme.palette.primary.white, minHeight: '100%', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header bar at the top */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBackClick} sx={{ color: default_theme.palette.other.main }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flex: 1 }}>
            Back to LAP Catalog
          </Typography>
          {isAuthenticated && (
            <IconButton
              onClick={() => toggleFavorite(lap.entryId)}
              sx={{ color: favorites.has(lap.entryId) ? "error.main" : "text.secondary" }}
            >
              {favorites.has(lap.entryId) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          )}
        </Box>

        {/* Main content area with sidebar and content */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "flex-start",
            }}
          >
            {/* Left sidebar - compact and theme-colored */}
            {visibleSideFields.length > 0 && selectedField && (
              <Box
                sx={{
                  width: 200,
                  bgcolor: default_theme.palette.other.backgroundGrey,
                  borderRadius: 2,
                  p: 1,
                  flexShrink: 0,
                  maxHeight: "80vh",
                  overflowY: "auto",
                  position: "relative",
                }}
              >
                {visibleSideFields.map((field) => {
                  const isActive = field.key === selectedField.key;
                  return (
                    <Box
                      key={field.key}
                      onClick={() => setSelectedFieldKey(field.key)}
                      sx={{
                        cursor: "pointer",
                        px: 1.5,
                        py: 0.75,
                        my: 0.25,
                        borderRadius: 1,
                        bgcolor: isActive
                          ? default_theme.palette.other.main
                          : "transparent",
                        color: isActive
                          ? default_theme.palette.primary.white
                          : default_theme.palette.text.primary,
                        fontSize: "0.8rem",
                        fontWeight: isActive ? "bold" : 500,
                        "&:hover": {
                          bgcolor: isActive
                            ? default_theme.palette.other.main
                            : default_theme.palette.other.tealHover,
                        },
                      }}
                    >
                      {field.label}
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box sx={{ flex: 1, minHeight: 200 }}>
              <Box
                sx={{
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h4" sx={{ flex: 1, fontWeight: "bold" }}>
                  {data.solution_title || "Untitled LAP"}
                </Typography>
                {/* <Chip label="LAP" size="medium" variant="outlined" /> */}
              </Box>

              {selectedField && (
                <>
                  <Typography
                    variant="h6"
                    sx={{ mb: 1, color: FIELD_COLOR, fontWeight: "bold" }}
                  >
                    {selectedField.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
                  >
                    {data[selectedField.key]}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LAPDetailPage;

